import {useAuth} from "../../../../context";
import {useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {fetchWrapper} from "../../../../_services/fetch";
import {centsToUnits, unitsToCents} from "../utilities/formatCurrency";
import * as yup from "yup";
import {endpoints, handleError, handleSuccess} from "../../../../utils2";

export function useCarrier(userId, caid = null) {
    const defaultValues = {
        id: null,
        company: "",
        name: "",
        price: "",
        created_at: null,
        updated_at: null,
        countries: [], //{id: null, name: "", code: ""}
        tracking_url: ""
    }

    const validationSchema = yup.object().shape({
        company: yup.string().required("Company cannot be empty").max(32),
        name: yup.string().required("Service cannot be empty").max(32),
        price: yup.number().required("Price cannot be empty").min(0),
        countries: yup.array().of(yup.object().shape({
            name: yup.string().required("Name cannot be empty")
        })).min(1, "Minimum 1 country"),
        tracking_url: yup.string().required("Tracking URL cannot be empty"),
    })


    const {authToken} = useAuth()
    const {value, setValue, loadingValue, setLoadingValue, errorValue, setErrorValue} = useCarrierDetails(caid, defaultValues)

    function handleSuccessInt(resp) {
        if (resp.success) {
            setValue(resp?.data ? {...resp.data, price: centsToUnits(resp.data.price_in_cents)} : defaultValues);
            return Promise.resolve(resp.message);
        } else {
            return Promise.reject({message: resp?.message || "Fetch failed with no error messages from the server."});
        }
    }

    const handleErrorInt = err => {
        setErrorValue(err)
        return Promise.reject(err)
    }

    function addItem(formValue) {
        const carrierInCents = {...formValue, price_in_cents: unitsToCents(formValue.price)}
        const {id, ...valuesNoId} = carrierInCents
        return fetchWrapper.post(endpoints.carrier.add(), valuesNoId, authToken.jwt)
            .then(handleSuccessInt)
            .catch(handleError)
            .catch(err => {
                setErrorValue()
                return Promise.reject(err)
            })
            .finally(() => setLoadingValue(false))
    }

    function editItem(formValue, targetId = caid) {
        const carrierInCents = {...formValue, price_in_cents: unitsToCents(formValue.price)}
        return fetchWrapper.put(endpoints.carrier.edit(targetId), carrierInCents, authToken.jwt)
            .then(handleSuccessInt)
            .catch(handleError)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function deleteItem(targetId = caid) {
        return fetchWrapper.delete(endpoints.carrier.delete(targetId), token.jwt)
            .then(handleSuccessInt)
            .catch(handleError)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    return {
        value,
        validationSchema,
        addItem,
        editItem,
        deleteItem,
        loadingValue,
        errorValue,
        clearErrorValue: () => setErrorValue(null)
    }
}

export function useCarrierDetails(carrierId, defaultValue) {

    const {loggedIn, authToken} = useAuth()
    const [loadingValue, setLoadingValue] = useState(true)
    const [errorValue, setErrorValue] = useState(null)
    const [value, setValue] = useState(defaultValue)

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && carrierId && carrierId !== "add") {
            setErrorValue(null)
            setLoadingValue(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${authToken.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpoints.carrier.get(carrierId), requestOptions)
                .then(handleSuccess)
                .then(data => setValue({...data, price: centsToUnits(data.price_in_cents)}))
                .catch(handleError)
                .catch(setErrorValue)
                .finally(() => setLoadingValue(false))
        } else {
            setLoadingValue(false)
        }

        return () => {
            controller.abort()
            setLoadingValue(false)
        }
    }, [loggedIn, carrierId]);

    return {
        value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}

export function useCarrierCount(queryOption = null) {

    const {loggedIn, authToken} = useAuth()
    const [searchParams] = useSearchParams()
    const [loadingCount, setLoadingCount] = useState(true)
    const [errorCount, setErrorCount] = useState(null)
    const [count, setCount] = useState(0)

    // Search options
    const query = queryOption || searchParams.get("query")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorCount(null)
            setLoadingCount(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${authToken.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {query}

            fetchWrapper.generic(endpoints.carrier.count(search), requestOptions)
                .then(handleSuccess)
                .then(setCount)
                .catch(handleError)
                .catch(setErrorCount)
                .finally(() => setLoadingCount(false))
        } else {
            setLoadingCount(false)
        }

        return () => {
            controller.abort()
            setLoadingCount(false)
        }
    }, [loggedIn, query]);

    return {
        count,
        setCount,
        loadingCount,
        errorCount,
        clearErrorCount: () => setErrorCount(null)
    }
}


export function useCarrierList(pageSizeOption = null, pageIndexOption = null, queryOption = null, orderByOption = null, keepPrev = false) {

    const {loggedIn, authToken} = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loadingList, setLoadingList] = useState(true)
    const [errorList, setErrorList] = useState(null)
    const [list, setList] = useState([])
    const [reload, setReload] = useState(0)

    // Search options
    const pageSize = pageSizeOption || Number(searchParams.get("size"))
    const pageIndex = pageIndexOption || Number(searchParams.get("index"))
    const query = queryOption || searchParams.get("query")
    const orderBy = orderByOption || searchParams.get("by")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && pageIndex != null && pageSize != null) {
            setErrorList(null)
            setLoadingList(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${authToken.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {
                offset: pageIndex * pageSize,
                limit: pageSize,
                query: query,
                order_by: orderBy
            }
            const convertPrice = data => data.map(item => ({...item, price: centsToUnits(item.price_in_cents)}))
            fetchWrapper.generic(endpoints.carrier.list(search), requestOptions)
                .then(handleSuccess)
                .then(data => setList(prev => keepPrev ? ([...prev, ...convertPrice(data)]) : convertPrice(data)))
                .catch(handleError)
                .catch(setErrorList)
                .finally(() => setLoadingList(false))
        } else {
            setLoadingList(false)
        }


        // return () => {
        //     controller.abort()
        //     setLoadingList(false)
        // }
    }, [loggedIn, pageIndex, pageSize, query, orderBy, reload])

    return {
        list,
        setList: fields => {
            setList(prev => {
                const fieldsNew = (typeof fields === "function") ? fields(prev) : fields
                if (fieldsNew.length === 0 && pageIndex > 0) {
                    setSearchParams(prev => {
                        prev.set("index", `${pageIndex - 1}`)
                    })
                } else if (fieldsNew.length < prev.length) {
                    setReload(reload + 1)
                }
                return [...fieldsNew]
            })
        },
        loadingList,
        errorList,
        clearErrorList: () => setErrorList(null)
    }
}

export function useCarrierOrderOptions(defaultOptions = []) {

    const {loggedIn, authToken} = useAuth()
    const [loadingOrderOptions, setLoadingOrderOptions] = useState(true)
    const [errorOrderOptions, setErrorOrderOptions] = useState(null)
    const [orderOptions, setOrderOptions] = useState(defaultOptions)

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorOrderOptions(null)
            setLoadingOrderOptions(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${authToken.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpoints.carrier.order(), requestOptions)
                .then(handleSuccess)
                .then(setOrderOptions)
                .catch(handleError)
                .catch(setErrorOrderOptions)
                .finally(() => setLoadingOrderOptions(false))
        } else {
            setLoadingOrderOptions(false)
        }

        return () => {
            controller.abort()
            setLoadingOrderOptions(false)
        }
    }, [loggedIn])


    return {
        orderOptions,
        setOrderOptions,
        loadingOrderOptions,
        setLoadingOrderOptions,
        errorOrderOptions,
        setErrorOrderOptions,
        clearErrorOrderOptions: () => setErrorOrderOptions(null)
    }
}